import { getRobotIconName, loadAllRobotImages } from '../../../../components/RobotOnMapEditable/robotMapUtils'
import { FeatureCollection, Point } from 'geojson'
import { useEffect, useMemo, useRef } from 'react'
import { Layer, Source, SymbolLayer } from 'react-map-gl'
import { ROBOT_NETWORK_STATUSES } from 'utils/constants'
import { getYawFromQuaternion } from 'utils/getYawFromQuaternion'
import { IRobotInfo } from 'utils/interfaces'
import { zoomMapTo } from 'utils/mapUtils'

const layerRobotStyle: SymbolLayer = {
  id: 'robot-image-style',
  type: 'symbol',
  layout: {
    'icon-image': ['get', 'icon'],
    'icon-anchor': 'center',
    'icon-rotation-alignment': 'map',
    'icon-size': ['interpolate', ['exponential', 2], ['zoom'], 15, 0.6, 22, 3.2],
    'icon-rotate': ['get', 'yaw'],
    'icon-allow-overlap': true,
  },
  paint: {
    'icon-opacity': ['get', 'opacity'],
  },
}

const AllRobotsOnMap = ({
  map,
  robots,
  onRobotClicked,
}: {
  map: mapboxgl.Map | undefined
  robots: IRobotInfo[]
  onRobotClicked: (id?: number) => void
}) => {
  const robotsOnMap: FeatureCollection<Point> = useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: robots
        .filter(robot => robot.lastPosition)
        .map(robot => {
          const icon = getRobotIconName(robot.vendor, robot.robot_color, robot.robot_type)

          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: robot.lastPosition?.coordinates || [],
            },
            properties: {
              opacity: robot.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE ? 1 : 0.5,
              id: robot.id,
              yaw: robot.lastOrientation ? getYawFromQuaternion(robot.lastOrientation) : 0,
              icon,
            },
          }
        }),
    }
  }, [robots])

  const hasZoomedRef = useRef(false)

  useEffect(() => {
    loadAllRobotImages(map)
    map?.on('style.load', () => loadAllRobotImages(map))

    // Only zoom once when map is ready and we have robots
    if (map && robotsOnMap.features.length > 0 && !hasZoomedRef.current) {
      zoomMapTo(map, robotsOnMap.features)
      hasZoomedRef.current = true
    } else if (robotsOnMap.features.length === 0) {
      hasZoomedRef.current = false
    }
  }, [map, robotsOnMap.features])

  const onRobotClickedOnMap = (e: any) => {
    if (e.features[0]) {
      onRobotClicked(e.features[0].properties.id)
    } else {
      console.warn('Here should be robot, but got nothing')
    }
  }

  const onNothingClicked = (e: any) => {
    // setHighlightedRobot(e?.features && e?.features[0].properties?.id_on_map)'
    onRobotClicked()
  }

  useEffect(() => {
    map?.on('click', onNothingClicked)
    map?.on('click', 'robot-image-style', onRobotClickedOnMap)
    return () => {
      map?.off('click', onNothingClicked)
      map?.off('click', 'robot-image-style', onRobotClickedOnMap)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map])

  return (
    <>
      {robotsOnMap && robotsOnMap.features.length > 0 && (
        <Source key={robotsOnMap.features.length} id="robot" type="geojson" data={robotsOnMap}>
          <Layer {...layerRobotStyle} />
        </Source>
      )}
    </>
  )
}

export default AllRobotsOnMap
